import { Button } from "@egaranti/components";
import { Input } from "@egaranti/components";
import { Dialog, DialogContent, DialogTrigger } from "@egaranti/components";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@egaranti/components";
import { ScrollArea } from "@egaranti/components";
import { Label } from "@egaranti/components";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ProductService from "@/services/productService";

import CategorySelector from "@/components/shared/CategorySelector";

import debounce from "lodash/debounce";
import {
  Filter,
  FilterXIcon,
  Loader2,
  LucideTrash2,
  Package,
  PlusCircleIcon,
} from "lucide-react";

const MAX_PRODUCTS = 10;
const DEBOUNCE_DELAY = 500;

export function ProductSelector({ onSelect = () => {}, selectedProducts = [] }) {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({
    mainCategoryId: "",
    categoryId: "",
    subCategoryId: "",
    subCategoryBrandId: "",
  });

  const [showFilters, setShowFilters] = useState(false);
  const [mainCategories, setMainCategories] = useState([]);

  const { t } = useTranslation();

  const loadProducts = useCallback(async (searchValue, currentFilters) => {
    setIsLoading(true);
    try {
      const response = await ProductService.getProducts({
        query: searchValue,
        page: 1,
        pageSize: 10,
        ...currentFilters,
      });
      setProducts(response?.content || []);
    } catch (error) {
      console.error("Failed to load products:", error);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedLoadProducts = useMemo(
    () =>
      debounce((searchValue, currentFilters) => {
        loadProducts(searchValue, currentFilters);
      }, DEBOUNCE_DELAY),
    [loadProducts],
  );

  useEffect(() => {
    return () => {
      debouncedLoadProducts.cancel();
    };
  }, [debouncedLoadProducts]);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchTerm(value);
      debouncedLoadProducts(value, filters);
    },
    [debouncedLoadProducts, filters],
  );

  useEffect(() => {
    debouncedLoadProducts(searchTerm, filters);
  }, [filters, debouncedLoadProducts, searchTerm]);

  useEffect(() => {
    const loadMainCategories = async () => {
      try {
        const { data } = await ProductService.getMainCategories();
        setMainCategories(data);
      } catch (error) {
        console.error("Failed to load main categories:", error);
      }
    };
    loadMainCategories();
  }, []);

  const handleSelect = useCallback(
    (product) => {
      if (selectedProducts.length >= MAX_PRODUCTS) return;
      const isAlreadySelected = selectedProducts.some(
        (p) => p.id === product.id,
      );
      if (!isAlreadySelected) {
        onSelect([...selectedProducts, product]);
      }
    },
    [selectedProducts, onSelect],
  );

  const handleRemove = useCallback(
    (productId) => {
      onSelect(selectedProducts.filter((p) => p.id !== productId));
    },
    [selectedProducts, onSelect],
  );

  const isProductSelected = useCallback(
    (productId) => {
      return selectedProducts.some((p) => p.id === productId);
    },
    [selectedProducts],
  );

  const isThereAnyFilters = useMemo(
    () => Object.values(filters).some((value) => value),
    [filters],
  );

  return (
    <div className="space-y-4">
      {/* Selected Products Display */}
      <div className="flex flex-col gap-2">
        {selectedProducts.length > 0 ? (
          <div className="rounded-lg border bg-gray-50/50 p-4">
            <Label className="text-sm font-medium text-gray-700">
              {t("productsPage.selectedProducts")} ({selectedProducts.length}/
              {MAX_PRODUCTS})
            </Label>
            <div className="grid grid-cols-1 gap-2 pt-4 md:grid-cols-3">
              {selectedProducts.map((product, index) => (
                <div
                  key={product.id}
                  className="group relative flex items-center gap-3 rounded-lg border bg-white p-3 transition-all"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                    {index + 1}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <h4 className="font-medium text-gray-900">
                      {product.name}
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {product.brand && (
                        <span className="truncate">{product.brand}</span>
                      )}
                      {product.category && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="truncate">{product.category}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    className="h-8 w-8 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(product.id);
                    }}
                  >
                    <LucideTrash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-gray-50/50 p-8">
            <Package className="h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-sm font-medium text-gray-900">
              {t("productsPage.noProductsSelected")}
            </h3>
            <p className="mt-1 text-center text-sm text-gray-500">
              {t("productsPage.selectProductsToStart")}
            </p>
          </div>
        )}

        {/* Add Product Button */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <button
              className="flex items-center bg-white py-4 font-medium text-[#0040C9] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={selectedProducts.length >= MAX_PRODUCTS}
            >
              <PlusCircleIcon className="mr-2 h-5 w-5" />
              {t("common.addProduct")}
            </button>
          </DialogTrigger>
          <DialogContent className="p-0 sm:max-w-[600px]">
            <Command className="rounded-lg" shouldFilter={false}>
              <div className="flex flex-col gap-2 border-b p-2">
                <div className="flex items-center gap-1">
                  <div className="relative flex-1">
                    <CommandInput
                      placeholder={t("productPage.searchProducts")}
                      value={searchTerm}
                      onValueChange={handleSearchChange}
                    />
                  </div>
                  <div className="mr-9 flex items-center gap-2">
                    <Button
                      variant="secondaryGray"
                      size="sm"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter className="h-4 w-4" />
                      {t("common.filter")}
                    </Button>
                    {isThereAnyFilters && (
                      <Button
                        size="sm"
                        variant="secondaryGray"
                        onClick={() => {
                          setFilters({});
                          setSearchTerm("");
                          setShowFilters(false);
                        }}
                        aria-label={t("common.resetFilters")}
                        className="border-red-500 text-red-500"
                      >
                        <FilterXIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {showFilters && (
                  <div className="duration-200 animate-in slide-in-from-top-2">
                    <CategorySelector
                      value={{
                        mainCategoryId: filters.mainCategoryId,
                        categoryId: filters.categoryId,
                        subCategoryId: filters.subCategoryId,
                        subCategoryBrandId: filters.subCategoryBrandId,
                      }}
                      onChange={(values) => {
                        setFilters((prev) => ({
                          ...prev,
                          ...values,
                        }));
                      }}
                    />
                  </div>
                )}
              </div>

              <CommandList>
                <CommandEmpty className="py-6 text-center text-sm">
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("common.loading")}
                    </div>
                  ) : (
                    t("productsPage.productCouldNotFound")
                  )}
                </CommandEmpty>
                <CommandGroup>
                  <ScrollArea className="max-h-[400px]">
                    {!isLoading &&
                      products.map((product) => {
                        const isSelected = isProductSelected(product.id);
                        return (
                          <CommandItem
                            key={product.id}
                            value={product.name}
                            onSelect={() => {
                              if (isSelected) {
                                handleRemove(product.id);
                              } else if (
                                selectedProducts.length < MAX_PRODUCTS
                              ) {
                                handleSelect(product);
                              }
                            }}
                            className={`flex cursor-pointer items-center justify-between p-3 ${
                              isSelected
                                ? "bg-blue-50/50 text-blue-700"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            <div className="flex flex-col gap-1">
                              <span className="font-medium">
                                {product.name}
                              </span>
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                {product.brand && (
                                  <span className="truncate">
                                    {product.brand}
                                  </span>
                                )}
                                {product.category && (
                                  <>
                                    <span className="text-gray-300">•</span>
                                    <span className="truncate">
                                      {product.category}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            {isSelected && (
                              <div className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                                {t("common.selected")}
                              </div>
                            )}
                          </CommandItem>
                        );
                      })}
                  </ScrollArea>
                </CommandGroup>
              </CommandList>
            </Command>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default ProductSelector;
